// 플랫폼 로그인 왕복의 규칙 — 순수 함수 (플랫폼 README 「MVP 가 로그인을 붙이는 법」)
//   1. 화면 → <플랫폼>/login?tenant=<mvp>&return_to=<지금 주소>
//   2. 플랫폼 → <지금 주소>#platform_token=<JWT>&expires_in=<초>

export interface IssuedToken {
  readonly token: string;
  /** 만료 시각 (epoch ms) */
  readonly expiresAt: number;
}

const TOKEN_KEY = 'platform_token';
const EXPIRES_KEY = 'expires_in';
// 플랫폼 토큰 수명 범위(60초~24시간) 밖의 값은 믿지 않는다
const MAX_EXPIRES_IN = 86_400;

export function loginUrl(platformUrl: string, tenant: string, returnTo: string): string {
  const query = new URLSearchParams({ tenant, return_to: returnTo });
  return `${platformUrl}/login?${query.toString()}`;
}

/** 로그인 뒤 돌아갈 주소 — 지금 주소에서 조각(#)을 뗀다. 조각에는 지난 토큰이 남아 있을 수 있다 */
export function returnToOf(href: string): string {
  const i = href.indexOf('#');
  return i < 0 ? href : href.slice(0, i);
}

export function toIssuedToken(token: unknown, expiresIn: unknown, now: number): IssuedToken | null {
  const seconds = typeof expiresIn === 'string' ? Number(expiresIn) : expiresIn;
  if (typeof token !== 'string' || token.split('.').length !== 3) return null;
  if (typeof seconds !== 'number' || !Number.isInteger(seconds) || seconds <= 0 || seconds > MAX_EXPIRES_IN) return null;
  return { token, expiresAt: now + seconds * 1000 };
}

/**
 * 주소 조각에서 토큰을 꺼낸다. 토큰이 없으면 null.
 * rest 는 토큰 관련 값을 뺀 나머지 조각 — 주소창에서 토큰을 지울 때 쓴다 (`#` 포함, 남는 것이 없으면 빈 문자열)
 */
export function readCallback(hash: string, now: number): { issued: IssuedToken; rest: string } | null {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const issued = toIssuedToken(params.get(TOKEN_KEY), params.get(EXPIRES_KEY), now);
  if (!issued) return null;
  params.delete(TOKEN_KEY);
  params.delete(EXPIRES_KEY);
  const rest = params.toString();
  return { issued, rest: rest ? `#${rest}` : '' };
}

/** 만료 얼마 전에 다시 받을지 — 수명의 10%, 최소 30초·최대 5분 */
export function refreshDelayMs(expiresAt: number, now: number): number {
  const life = expiresAt - now;
  const margin = Math.min(300_000, Math.max(30_000, life * 0.1));
  return Math.max(0, life - margin);
}
